import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import confetti from 'canvas-confetti';
import { ChevronLeft, Check, Sparkles } from 'lucide-react';
import { useCourse, isStepUnlocked, canStartToday } from '@/hooks/useCourse';
import { useUserStore } from '@/store/userStore';
import { useTelegram } from '@/hooks/useTelegram';
import { Loader } from '@/components/Loader';
import { Mascot } from '@/components/Mascot';

// ════════════════════════════════════════════════════════════════
// Экран одного шага (Фаза 2/3, базовый)
// Слава → задание → чипсы → [Сделать] запускает фичу → «почему» →
// фиксация ощущения → [Готово] продвигает прогресс.
// ════════════════════════════════════════════════════════════════

const HUNGER = [
  { v: 'ещё голодно', e: '😋' },
  { v: 'в самый раз', e: '🙂' },
  { v: 'сытно', e: '😌' },
  { v: 'тяжесть', e: '🫃' },
];
const MOOD = [
  { v: 'спокойно', e: '😌' },
  { v: 'нейтрально', e: '😐' },
  { v: 'тревожно', e: '😟' },
  { v: 'на эмоциях', e: '🌊' },
];
const PHRASES = [
  'Маленький шаг — а из них и складываются большие перемены 💚',
  'Ещё один шаг к себе. Горжусь тобой!',
  'День засчитан. Так и рождается привычка 🌱',
  'Ты сделал это. Маленькое действие — большая разница.',
  'Плюс один день. Ты в потоке 🔥',
];

export default function StepScreen() {
  const nav = useNavigate();
  const { day } = useParams();
  const dayNum = Number(day);
  const { profile } = useUserStore();
  const { haptic } = useTelegram();
  const { steps, progress, loading, completeStep } = useCourse();
  const [fixation, setFixation] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [celebrating, setCelebrating] = useState(false);
  const [phrase, setPhrase] = useState('');

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center"><Loader label="Открываем шаг…" /></div>;
  }

  const step = steps.find((s) => s.day === dayNum);
  if (!step) {
    return (
      <div className="px-5 pt-6 max-w-md mx-auto">
        <Back onBack={() => nav('/path')} />
        <p className="text-sm text-ink-dim mt-6">Шаг не найден.</p>
      </div>
    );
  }

  const unlocked = isStepUnlocked(step, profile?.subscription_status);
  const alreadyDone = (progress?.completed_days ?? []).includes(dayNum);

  if (!unlocked && !alreadyDone) {
    return (
      <div className="px-5 pt-6 pb-28 max-w-md mx-auto">
        <Back onBack={() => nav('/path')} />
        <div className="card-elevated p-6 text-center mt-6">
          <div className="text-3xl mb-2">🔒</div>
          <h1 className="font-display text-xl font-bold mb-2">Продолжение — по подписке</h1>
          <p className="text-sm text-ink-dim mb-5">Первые 3 дня Пути бесплатны. Дальше открывается с подпиской.</p>
          <button onClick={() => nav('/paywall')} className="w-full py-3 rounded-xl bg-emerald-glow text-bg font-bold active:scale-[0.98]">
            Открыть весь Путь
          </button>
        </div>
      </div>
    );
  }

  // Пейсинг: один шаг в день — будущий/следующий день откроется позже
  const cur = progress?.current_day ?? 1;
  const notYet = step.day > cur || (!alreadyDone && step.day === cur && !canStartToday(progress ?? null));
  if (notYet) {
    return (
      <div className="px-5 pt-6 pb-28 max-w-md mx-auto">
        <Back onBack={() => nav('/path')} />
        <div className="card-elevated p-6 text-center mt-6">
          <div className="text-3xl mb-2">🌙</div>
          <h1 className="font-display text-xl font-bold mb-2">Шаг откроется завтра</h1>
          <p className="text-sm text-ink-dim mb-5">Один шаг в день — так привычка укореняется крепче. Возвращайся завтра за днём {cur}.</p>
          <button onClick={() => nav('/path')} className="w-full py-3 rounded-xl bg-emerald-glow text-bg font-bold active:scale-[0.98]">Вернуться к Пути</button>
        </div>
      </div>
    );
  }

  // Запуск реальной фичи по типу действия
  function launchAction() {
    haptic('medium');
    switch (step!.action_type) {
      case 'photo_meal': nav('/add?mode=photo'); break;
      case 'breathing': nav(`/breathing/${step!.action_ref ?? 'box'}`); break;
      case 'meditation': nav(step!.action_ref ? `/relax/player/${step!.action_ref}` : '/relax'); break;
      case 'measure': nav('/profile'); break;
      default: /* reflection / none — остаёмся на экране */ break;
    }
  }

  async function done() {
    setSaving(true);
    haptic('success');
    try {
      const fix = fixation ? { [step!.fixation_type]: fixation } : undefined;
      await completeStep(dayNum, fix);
      if (dayNum >= 21) { nav('/path/milestone'); return; }
      // Поощрение: маленький праздник за выполненный шаг
      setPhrase(PHRASES[Math.floor(Math.random() * PHRASES.length)]);
      confetti({ particleCount: 90, spread: 70, origin: { y: 0.5 }, colors: ['#10b981', '#06b6d4', '#84cc16'] });
      setCelebrating(true);
    } finally {
      setSaving(false);
    }
  }

  const fixChips = step.fixation_type === 'hunger_fullness' ? HUNGER : step.fixation_type === 'mood' ? MOOD : null;
  const actionLabel: Record<string, string> = {
    photo_meal: '📸 Сфотографировать приём', breathing: '🫁 Начать дыхание',
    meditation: '🧘 Открыть медитацию', measure: '📏 К замерам', reflection: '', photo_face: '', none: '',
  };

  return (
    <div className="px-5 pt-6 pb-28 max-w-md mx-auto">
      {celebrating && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="fixed inset-0 z-50 bg-bg/95 backdrop-blur-sm flex flex-col items-center justify-center px-8 text-center"
        >
          <motion.div initial={{ scale: 0.6, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} transition={{ type: 'spring', damping: 12 }} className="mb-3">
            <Mascot pose="celebrate" className="w-40 h-40" float={false} />
          </motion.div>
          <h2 className="font-display text-2xl font-bold mb-2">Шаг выполнен!</h2>
          <p className="text-sm text-ink-dim mb-8 max-w-xs leading-relaxed">{phrase}</p>
          <button
            onClick={() => nav('/path')}
            className="w-full max-w-xs py-3.5 rounded-xl bg-emerald-glow text-bg font-bold active:scale-[0.98]"
          >
            Продолжить
          </button>
        </motion.div>
      )}

      <Back onBack={() => nav('/path')} title={`День ${step.day}`} />

      <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="mt-4 space-y-3">
        {/* Слава */}
        <div className="flex gap-2.5 items-start bg-bg-elevated/60 border border-line rounded-xl p-3">
          <Mascot pose="calm" compact float={false} className="w-8 h-8 shrink-0" />
          <p className="text-sm leading-relaxed">{step.slava_line}</p>
        </div>

        {/* Описание — узнавание себя + почему это работает + приём */}
        {step.description && (
          <p className="text-sm leading-relaxed text-ink whitespace-pre-line">{step.description}</p>
        )}

        {/* Задание */}
        <h1 className="font-display text-xl font-bold leading-tight">{step.task}</h1>

        {/* Чипсы «как» */}
        {step.how_chips?.length > 0 && (
          <div className="flex flex-wrap gap-1.5">
            {step.how_chips.map((c) => (
              <span key={c} className="text-xs bg-bg-elevated border border-line rounded-full px-2.5 py-1 text-ink-muted">{c}</span>
            ))}
          </div>
        )}

        {/* Пример — конкретно, что сделать */}
        {step.example && (
          <div className="text-sm bg-emerald-glow/8 border border-emerald-glow/25 rounded-lg px-3 py-2.5 leading-relaxed">
            <span className="font-semibold text-emerald-glow">💡 Например:</span> {step.example}
          </div>
        )}

        {/* Запуск фичи */}
        {actionLabel[step.action_type] && (
          <button onClick={launchAction} className="w-full py-3 rounded-xl bg-emerald-glow text-bg font-bold active:scale-[0.98] transition-transform">
            {actionLabel[step.action_type]} →
          </button>
        )}

        {step.action_type === 'breathing' && (
          <p className="text-[11px] text-ink-dim leading-relaxed">
            🎵 Хочешь атмосферу — сначала включи трек в разделе «Медитации» (он продолжит играть фоном), потом вернись и начни дыхание.
          </p>
        )}

        {/* Почему это работает */}
        {step.why_text && (
          <div className="text-xs text-ink-dim bg-bg-elevated/50 border border-line rounded-lg px-3 py-2.5 leading-relaxed">
            {step.why_text}
            {step.source_ref && (
              <span className="inline-flex items-center gap-1 ml-1 text-emerald-glow font-medium">
                <Sparkles size={11} /> {step.strength} · {step.source_ref}
              </span>
            )}
          </div>
        )}

        {/* Фиксация ощущения */}
        {fixChips && (
          <div className="pt-1">
            <div className="text-xs text-ink-dim mb-1.5">
              {step.fixation_type === 'hunger_fullness' ? 'Насколько ты сыт? Просто заметь тело — правильного ответа нет.' : 'Как ты сейчас?'}
            </div>
            <div className="grid grid-cols-2 gap-1.5">
              {fixChips.map((c) => (
                <button
                  key={c.v}
                  onClick={() => { haptic('light'); setFixation(c.v); }}
                  className={[
                    'py-2 rounded-lg border text-xs transition-all',
                    fixation === c.v ? 'bg-emerald-glow/15 border-emerald-glow/50 text-ink' : 'bg-bg-card border-line text-ink-muted',
                  ].join(' ')}
                >
                  <span className="mr-1">{c.e}</span>{c.v}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Готово */}
        <button
          onClick={done}
          disabled={saving}
          className="w-full py-3 rounded-xl bg-emerald-glow/15 border-2 border-emerald-glow/50 text-emerald-glow font-bold flex items-center justify-center gap-1.5 active:scale-[0.98] disabled:opacity-50"
        >
          <Check size={16} />
          {saving ? 'Сохраняем…' : alreadyDone ? 'Пройдено ✓ (можно повторить)' : 'Готово, шаг выполнен'}
        </button>
      </motion.div>
    </div>
  );
}

function Back({ onBack, title }: { onBack: () => void; title?: string }) {
  return (
    <div className="flex items-center gap-2">
      <button onClick={onBack} className="w-9 h-9 -ml-2 rounded-full flex items-center justify-center text-ink-dim active:bg-bg-elevated">
        <ChevronLeft size={20} />
      </button>
      {title && <div className="font-display font-bold text-lg">{title}</div>}
    </div>
  );
}
